import React from "react";
import { CheckCircle2, Clock3, RefreshCw, ServerCrash, TriangleAlert } from "lucide-react";

import { cn, formatRelativeTime } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";

const getStatusStyles = (status) => {
  switch (status) {
    case "healthy":
    case "available":
      return {
        pill: "border-emerald-400/20 bg-emerald-400/10 text-emerald-300",
        icon: CheckCircle2,
      };
    case "attention":
    case "rate_limited":
    case "no_api_key":
      return {
        pill: "border-amber-400/20 bg-amber-400/10 text-amber-300",
        icon: TriangleAlert,
      };
    case "failed":
    case "unavailable":
      return {
        pill: "border-rose-400/20 bg-rose-400/10 text-rose-300",
        icon: ServerCrash,
      };
    default:
      return {
        pill: "border-zinc-700 bg-zinc-800/80 text-zinc-300",
        icon: Clock3,
      };
  }
};

const StatusBadge = ({ status, label }) => {
  const styles = getStatusStyles(status);
  const Icon = styles.icon;

  return (
    <div className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em]", styles.pill)}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
  );
};

const SummaryCard = ({ label, value, detail, status }) => (
  <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <StatusBadge status={status} label={status.replace(/_/g, " ")} />
    </div>
    <p className="mt-4 font-mono text-2xl text-zinc-50">{value}</p>
    <p className="mt-1 text-sm font-['Manrope'] text-zinc-500">{detail}</p>
  </div>
);

const LoadingState = () => (
  <div className="space-y-4">
    <div className="grid gap-3 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
          <Skeleton className="h-3 w-24 bg-white/10" />
          <Skeleton className="mt-4 h-6 w-20 bg-white/10" />
          <Skeleton className="mt-2 h-3 w-28 bg-white/10" />
        </div>
      ))}
    </div>
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24 bg-white/10" />
              <Skeleton className="h-3 w-32 bg-white/10" />
            </div>
            <Skeleton className="h-6 w-24 rounded-full bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-10 text-center">
    <p className="font-['Outfit'] text-zinc-50">No job health telemetry yet</p>
    <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
      Backfill and crawl health will appear once providers or sourcing runs are available.
    </p>
  </div>
);

const JobHealthPanel = ({ jobHealth, loading }) => {
  const providerCount = jobHealth?.totalProviderCount || 0;
  const availableProviders = jobHealth?.availableProviderCount || 0;
  const providers = jobHealth?.providers || [];
  const backfill = jobHealth?.backfill || {};
  const database = jobHealth?.database || {};
  const hasData = providerCount > 0 || Boolean(backfill.startedAt || backfill.completedAt || database.label);

  return (
    <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-5 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Job Health</p>
          <h2 className="mt-2 font-['Outfit'] text-xl text-zinc-50">Crawl and backfill health</h2>
        </div>
        <div className="rounded-full border border-zinc-800 bg-zinc-950/70 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-400">
          Provider + run telemetry
        </div>
      </div>

      <p className="mt-3 text-sm font-['Manrope'] text-zinc-500">
        Queue-level job counters are not exposed yet, so this panel uses database health, provider status, and the latest sourcing run.
      </p>

      <div className="mt-5">
        {loading ? (
          <LoadingState />
        ) : hasData ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <SummaryCard
                label="Database"
                value={database.label === "connected" ? "Connected" : database.label || "Unknown"}
                detail={database.checkedAt ? `Checked ${formatRelativeTime(database.checkedAt)}` : "Waiting for health ping"}
                status={database.status || "unknown"}
              />
              <SummaryCard
                label="Providers"
                value={`${availableProviders}/${providerCount}`}
                detail={providerCount ? "Available providers" : "No provider status yet"}
                status={availableProviders === providerCount && providerCount > 0 ? "available" : availableProviders > 0 ? "attention" : "unknown"}
              />
              <SummaryCard
                label="Backfill"
                value={backfill.completedAt || backfill.startedAt ? `${backfill.updatedCount} upd` : "No runs"}
                detail={
                  backfill.completedAt
                    ? `Last run ${formatRelativeTime(backfill.completedAt)}`
                    : backfill.startedAt
                      ? `Started ${formatRelativeTime(backfill.startedAt)}`
                      : "No sourcing run recorded yet"
                }
                status={backfill.status || "empty"}
              />
            </div>

            {providers.length > 0 ? (
              <div className="space-y-3">
                {providers.map((provider) => (
                  <div key={provider.key} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-['Outfit'] text-zinc-50">{provider.name}</p>
                        <p className="mt-1 text-sm font-['Manrope'] text-zinc-500">
                          {provider.error
                            ? provider.error
                            : `${provider.requestCount.toLocaleString()} requests · ${provider.errorCount.toLocaleString()} errors`}
                        </p>
                      </div>
                      <StatusBadge status={provider.status} label={provider.status.replace(/_/g, " ")} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-6 text-sm font-['Manrope'] text-zinc-500">
                Provider health has not been reported yet.
              </div>
            )}

            {(backfill.completedAt || backfill.startedAt) && (
              <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-zinc-300">
                  <RefreshCw className="h-4 w-4" />
                  <p className="font-['Outfit'] text-zinc-50">Latest sourcing run</p>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Scraped</p>
                    <p className="mt-2 font-mono text-lg text-zinc-50">{backfill.scrapedCount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">New</p>
                    <p className="mt-2 font-mono text-lg text-zinc-50">{backfill.newCount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Updated</p>
                    <p className="mt-2 font-mono text-lg text-zinc-50">{backfill.updatedCount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">Errors</p>
                    <p className="mt-2 font-mono text-lg text-zinc-50">{backfill.errorCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};

export default JobHealthPanel;
